import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CaixaResumo, Cliente, ConferenciaEntrada, Contagem, ContagemAiProdutoDisponivel, ContagemAiSugestao, ContagemAiUsuarioDisponivel, ContagemFotoUpload, ContaPix, ContasFinanceirasResumo, DashboardResumo, DashboardResponsavelDetalhe, Fornecedor, NotaRetirada, NotaTransferencia, OcorrenciaResolucao, OcorrenciaRetirada, PagamentoPix, PagamentoRetirada, Produto, Usuario } from './models';

const API = '/api';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);

  dashboard(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<DashboardResumo>(`${API}/contagens/dashboard/resumo`, { params });
  }

  dashboardResponsavelDetalhe(responsavel: string) {
    return this.http.get<DashboardResponsavelDetalhe>(`${API}/contagens/dashboard/responsavel-detalhe`, {
      params: { responsavel }
    });
  }

  clientes() {
    return this.http.get<Cliente[]>(`${API}/clientes`);
  }

  salvarCliente(cliente: Partial<Cliente>) {
    return cliente.id_cliente
      ? this.http.put<Cliente>(`${API}/clientes/${cliente.id_cliente}`, cliente)
      : this.http.post<Cliente>(`${API}/clientes`, cliente);
  }

  fornecedores() {
    return this.http.get<Fornecedor[]>(`${API}/fornecedores`);
  }

  salvarFornecedor(fornecedor: Partial<Fornecedor>) {
    return fornecedor.id_fornecedor
      ? this.http.put<Fornecedor>(`${API}/fornecedores/${fornecedor.id_fornecedor}`, fornecedor)
      : this.http.post<Fornecedor>(`${API}/fornecedores`, fornecedor);
  }

  inativarFornecedor(id: number) {
    return this.http.delete<Fornecedor>(`${API}/fornecedores/${id}`);
  }

  inativarCliente(id: number) {
    return this.http.delete<Cliente>(`${API}/clientes/${id}`);
  }

  notas(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<NotaTransferencia[]>(`${API}/notas`, { params });
  }

  nota(id: number) {
    return this.http.get<NotaTransferencia>(`${API}/notas/${id}`);
  }

  salvarNota(nota: Partial<NotaTransferencia>) {
    return nota.id_nota
      ? this.http.put<NotaTransferencia>(`${API}/notas/${nota.id_nota}`, nota)
      : this.http.post<NotaTransferencia>(`${API}/notas`, nota);
  }

  finalizarNota(id: number, payload: { observacao_divergencia?: string } = {}) {
    return this.http.post<NotaTransferencia>(`${API}/notas/${id}/finalizar`, payload);
  }

  reabrirNota(id: number) {
    return this.http.post<NotaTransferencia>(`${API}/notas/${id}/reabrir`, {});
  }

  cancelarNota(id: number) {
    return this.http.post<NotaTransferencia>(`${API}/notas/${id}/cancelar`, {});
  }

  retiradas(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<NotaRetirada[]>(`${API}/notas/retiradas`, { params });
  }

  contagensDisponiveisRetirada() {
    return this.http.get<Contagem[]>(`${API}/notas/retiradas/contagens-disponiveis`);
  }

  registrarRetiradaPorContagens(retirada: Partial<NotaRetirada>) {
    return this.http.post<NotaRetirada[]>(`${API}/notas/retiradas`, retirada);
  }

  editarRetirada(idRetirada: number, retirada: Partial<NotaRetirada>) {
    return this.http.put<NotaRetirada>(`${API}/notas/retiradas/${idRetirada}`, retirada);
  }

  excluirRetirada(idRetirada: number) {
    return this.http.delete<NotaRetirada & { excluida?: boolean }>(`${API}/notas/retiradas/${idRetirada}`);
  }

  registrarRetirada(id: number, retirada: Partial<NotaRetirada>) {
    return this.http.post<NotaRetirada>(`${API}/notas/${id}/retiradas`, retirada);
  }

  abrirRetirada(idNota: number, idRetirada: number) {
    this.http.get(`${API}/notas/${idNota}/retiradas/${idRetirada}/pdf`, { responseType: 'blob' }).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    });
  }

  produtos() {
    return this.http.get<Produto[]>(`${API}/produtos`);
  }

  salvarProduto(produto: Partial<Produto>) {
    return produto.cod_produto
      ? this.http.put<Produto>(`${API}/produtos/${produto.cod_produto}`, produto)
      : this.http.post<Produto>(`${API}/produtos`, produto);
  }

  inativarProduto(id: number) {
    return this.http.delete<Produto>(`${API}/produtos/${id}`);
  }

  conferenciasEntrada(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<ConferenciaEntrada[]>(`${API}/conferencias-entrada`, { params });
  }

  conferenciaEntrada(id: number) {
    return this.http.get<ConferenciaEntrada>(`${API}/conferencias-entrada/${id}`);
  }

  salvarConferenciaEntrada(conferencia: Partial<ConferenciaEntrada>) {
    return conferencia.id_conferencia
      ? this.http.put<ConferenciaEntrada>(`${API}/conferencias-entrada/${conferencia.id_conferencia}`, conferencia)
      : this.http.post<ConferenciaEntrada>(`${API}/conferencias-entrada`, conferencia);
  }

  finalizarConferenciaEntrada(id: number) {
    return this.http.post<ConferenciaEntrada>(`${API}/conferencias-entrada/${id}/finalizar`, {});
  }

  cancelarConferenciaEntrada(id: number) {
    return this.http.post<ConferenciaEntrada>(`${API}/conferencias-entrada/${id}/cancelar`, {});
  }

  abrirConferenciaEntrada(id: number) {
    this.http.get(`${API}/conferencias-entrada/${id}/pdf`, { responseType: 'blob' }).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    });
  }

  usuarios() {
    return this.http.get<Usuario[]>(`${API}/usuarios`);
  }

  salvarUsuario(usuario: Partial<Usuario> & { senha?: string }) {
    return usuario.id_usuario
      ? this.http.put<Usuario>(`${API}/usuarios/${usuario.id_usuario}`, usuario)
      : this.http.post<Usuario>(`${API}/usuarios`, usuario);
  }

  inativarUsuario(id: number) {
    return this.http.delete<Usuario>(`${API}/usuarios/${id}`);
  }

  meuPerfil() {
    return this.http.get<Usuario>(`${API}/usuarios/me`);
  }

  atualizarMeuPerfil(perfil: { nome: string; cpf?: string }) {
    return this.http.put<Usuario>(`${API}/usuarios/me`, perfil);
  }

  alterarMinhaSenha(senhas: { senha_atual: string; nova_senha: string }) {
    return this.http.put<Usuario>(`${API}/usuarios/me/senha`, senhas);
  }

  contagens(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<Contagem[]>(`${API}/contagens`, { params });
  }

  contagem(id: number) {
    return this.http.get<Contagem>(`${API}/contagens/${id}`);
  }

  salvarContagem(contagem: Partial<Contagem> & { rascunho_foto?: ContagemFotoUpload | null; remover_rascunho?: boolean }) {
    return contagem.id_contagem
      ? this.http.put<Contagem>(`${API}/contagens/${contagem.id_contagem}`, contagem)
      : this.http.post<Contagem>(`${API}/contagens`, contagem);
  }

  anexoContagem(idContagem: number, idAnexo: number) {
    return this.http.get(`${API}/contagens/${idContagem}/anexos/${idAnexo}`, { responseType: 'blob' });
  }

  contagemIaConfig() {
    return this.http.get<{ enabled: boolean }>(`${API}/contagens/ia/config`);
  }

  extrairContagemFoto(payload: { imagem: string; produtos: ContagemAiProdutoDisponivel[]; usuarios: ContagemAiUsuarioDisponivel[] }) {
    return this.http.post<ContagemAiSugestao>(`${API}/contagens/ia/extrair-foto`, payload);
  }

  finalizarContagem(id: number) {
    return this.http.post<Contagem>(`${API}/contagens/${id}/finalizar`, {});
  }

  reabrirContagem(id: number) {
    return this.http.post<Contagem>(`${API}/contagens/${id}/reabrir`, {});
  }

  cancelarContagem(id: number) {
    return this.http.post<Contagem>(`${API}/contagens/${id}/cancelar`, {});
  }

  excluirContagem(id: number) {
    return this.http.delete<Contagem>(`${API}/contagens/${id}`);
  }

  abrirRomaneio(id: number) {
    this.http.get(`${API}/contagens/${id}/romaneio`, { responseType: 'blob' }).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    });
  }

  pagamentos(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<PagamentoRetirada[]>(`${API}/pagamentos`, { params });
  }

  salvarPagamento(pagamento: Partial<PagamentoRetirada> & { observacao?: string }) {
    return pagamento.id_pagamento
      ? this.http.put<PagamentoRetirada>(`${API}/pagamentos/${pagamento.id_pagamento}`, pagamento)
      : this.http.post<PagamentoRetirada>(`${API}/pagamentos`, pagamento);
  }

  finalizarPagamento(id: number) {
    return this.http.post<PagamentoRetirada[]>(`${API}/pagamentos/${id}/finalizar`, {});
  }

  reabrirPagamento(id: number) {
    return this.http.post<PagamentoRetirada[]>(`${API}/pagamentos/${id}/reabrir`, {});
  }

  excluirPagamento(id: number) {
    return this.http.delete<{ removidos: number }>(`${API}/pagamentos/${id}`);
  }

  abrirPagamento(id: number) {
    this.http.get(`${API}/pagamentos/${id}/pdf`, { responseType: 'blob' }).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    });
  }

  pagamentoPix(id: number) {
    return this.http.get<PagamentoPix[]>(`${API}/pagamentos/${id}/pix`);
  }

  marcarPagamentoPixPago(id: number, responsavel: string, pago = true) {
    return this.http.post<PagamentoPix[]>(`${API}/pagamentos/${id}/pix/pago`, { responsavel, pago });
  }

  caixa(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<CaixaResumo>(`${API}/caixa`, { params });
  }

  salvarLancamentoCaixa(lancamento: Record<string, unknown> & { id_caixa?: number }) {
    return lancamento.id_caixa
      ? this.http.put(`${API}/caixa/${lancamento.id_caixa}`, lancamento)
      : this.http.post(`${API}/caixa`, lancamento);
  }

  excluirLancamentoCaixa(id: number) {
    return this.http.delete(`${API}/caixa/${id}`);
  }

  contasFinanceiras(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<ContasFinanceirasResumo>(`${API}/contas-financeiras`, { params });
  }

  salvarContaFinanceira(conta: Record<string, unknown> & { id_conta?: number }) {
    return conta.id_conta
      ? this.http.put(`${API}/contas-financeiras/${conta.id_conta}`, conta)
      : this.http.post(`${API}/contas-financeiras`, conta);
  }

  baixarContaFinanceira(id: number, data: Record<string, unknown>) {
    return this.http.post(`${API}/contas-financeiras/${id}/baixar`, data);
  }

  contaFinanceiraPix(id: number, tipoPagamento = 'PIX') {
    return this.http.get<ContaPix>(`${API}/contas-financeiras/${id}/pix`, { params: { tipo_pagamento: tipoPagamento } });
  }

  cancelarContaFinanceira(id: number) {
    return this.http.post(`${API}/contas-financeiras/${id}/cancelar`, {});
  }

  reabrirContaFinanceira(id: number) {
    return this.http.post(`${API}/contas-financeiras/${id}/reabrir`, {});
  }

  excluirContaFinanceira(id: number) {
    return this.http.delete(`${API}/contas-financeiras/${id}`);
  }

  ocorrencias(filtros: Record<string, string | number | null | undefined> = {}) {
    let params = new HttpParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<OcorrenciaRetirada[]>(`${API}/ocorrencias`, { params });
  }

  resolverOcorrencia(id: number, payload: {
    observacao?: string;
    conferencias?: { id_item: number; quantidade: number }[];
    contagens?: { id_item: number; quantidade: number }[];
  } = {}) {
    return this.http.post<OcorrenciaRetirada>(`${API}/ocorrencias/${id}/resolver`, payload);
  }

  ocorrenciaResolucao(id: number) {
    return this.http.get<OcorrenciaResolucao>(`${API}/ocorrencias/${id}/resolucao`);
  }
}
